import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { classNames } from 'primereact/utils';
import * as React from 'react';
import { getMimeTypesList, MimeType } from '../../utils/upload';
import { FileUploadAndStatus } from '../../store/upload';
import { parseFileReadableFilesize } from '../../utils/parse';
import { Button } from 'primereact/button';

export default function FileUploader({
  className,
  onAddFile,
  files = [],
  allowedMimeTypes = [MimeType.XLS, MimeType.XLSX, MimeType.PDF, MimeType.JPG],
}: {
  allowedMimeTypes?: MimeType[],
  className?: string,
  filesListTemplate?: () => React.ReactNode
  files?: FileUploadAndStatus[],
  onAddFile: (file: File[]) => void,
}) {
  const uploadRef = React.useRef<FileUpload>(null);
  const [isDraggedOver, setIsDraggedOver] = React.useState<boolean>(false);

  function processUploadedFile(files: File[] ) {
    onAddFile(files);
  }

  function removeFile(file: File) {
    onAddFile([
      ...files.filter((_file) => _file !== file),
    ]);
  }

  function selectFile(e: FileUploadSelectEvent) {
    uploadRef?.current?.clear();
    processUploadedFile(Array.from(e.files));
  }

  function addFile(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDraggedOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    processUploadedFile(droppedFiles);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDraggedOver(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDraggedOver(false);
  }

  return (
    <section
      className={
        classNames(
          className,
          'flex flex-col items-center gap-4 border border-dashed p-6 rounded-lg',
          {
            'border-allstate-light-blue outline outline-allstate-light-blue -outline-offset-1': isDraggedOver,
            'border-grey-4': !isDraggedOver,
          }
        )
      }
      onDrop={addFile}
      onDragEnter={onDragOver}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDragExit={onDragLeave}
    >
      <p className="font-bold">Drag &amp; drop files here or</p>
      <FileUpload
        ref={uploadRef}
        mode="basic"
        chooseLabel="browse your files"
        onSelect={selectFile}
        multiple        
        accept={getMimeTypesList(allowedMimeTypes)}
      />
      <div className="text-center text-grey-8">
        {/* Max file size: <strong>10MB</strong><br /> */}
        Supported file types: <strong>{allowedMimeTypes.join(', ')}</strong>
      </div>

      {files && files.length > 0 && (
        <div className="flex flex-col w-full gap-2 px-4 pt-2 pb-4 rounded bg-grey-2">
          <h3 className="font-bold">Files uploaded</h3>

          <div className="grid gap-4">
            {files.map((file, index) => {
              return (
                <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-lg bg-white px-2 py-3 border border-grey-5" key={index}>
                  <div className="flex items-center justify-center w-8 h-8 rounded bg-accent-green">📊</div>
                  <div className="flex flex-col flex-1 gap-1 overflow-hidden">
                    <div className="flex text-sm gap-0.5">
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{file.name}</span>
                      <span className="text-grey-6">{parseFileReadableFilesize(file.size)}</span>
                    </div>
                  </div>
                  <div>
                    <Button rounded icon="pi pi-times" severity="secondary" size="small" onClick={() => removeFile(file)} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}